import React, { Key } from 'react';
import { Card, Empty, List } from 'antd';
import clsx from 'clsx';
import File from '../../interfaces/File';
import FileIcon from '../Common/FileIcon';
import TruncatedTooltip from '../Common/TruncatedTooltip';
import { getFileTitle, removeShortcutExtension } from '../../utils/utils';
import { Icon } from '../Core/Icon';
import TableEmptyState from '../../interfaces/TableEmptyState';
interface FilesListProps {
  files: File[];
  isLoading: boolean;
  handleLoadMore: () => void;
  handleFileRowClick: (
    file: File,
    hasClickedOnFilename: boolean,
    event?: React.MouseEvent
  ) => void;
  handleRowClick: (file: File, event?: React.MouseEvent) => void;
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: Key[]) => void;
  handleRowContextMenu: (record: File, event: React.MouseEvent) => void;
  handleRowDoubleClick: (file: File) => void;
  emptyState: TableEmptyState;
}

const FilesList: React.FC<FilesListProps> = ({
  files,
  isLoading,
  handleLoadMore,
  handleRowClick,
  selectedTableKeys,
  setSelectedTableKeys,
  handleRowContextMenu,
  handleRowDoubleClick,
  handleFileRowClick,
  emptyState,
}) => {
  return (
    <div
      style={{ height: 'calc(100vh-150px)', overflowY: 'auto' }}
      onScroll={(e) => {
        const scrollableElement = e.target as HTMLElement;
        if (
          scrollableElement.scrollTop + scrollableElement.clientHeight >=
          scrollableElement.scrollHeight - 100
        ) {
          handleLoadMore();
        }
      }}
    >
      <List
        loading={isLoading}
        grid={{
          gutter: 16,
          xs: 2,
          sm: 2,
          md: 3,
          lg: 4,
          xl: 5,
          xxl: 6,
        }}
        locale={{
          emptyText: (
            <Empty
              image={
                <Icon
                  name={emptyState.iconName}
                  className="h-28 w-28 text-gray-medium"
                />
              }
              description={
                <div className="flex flex-col items-center gap-2">
                  <span className="text-gray-dark">{emptyState.label}</span>
                </div>
              }
            />
          ),
        }}
        dataSource={files}
        className="h-[calc(100vh-150px)] p-2"
        renderItem={(item: File) => (
          <List.Item key={item.uid}>
            <Card
              variant="borderless"
              className={clsx(
                'group relative rounded-xl p-2 shadow-sm transition-all duration-300 hover:bg-gray-light',
                selectedTableKeys.includes(item.uid)
                  ? 'border-blue-500 border bg-gray-light'
                  : ''
              )}
              hoverable
              onClick={(event: React.MouseEvent) => handleRowClick(item, event)}
              onDoubleClick={() => handleRowDoubleClick(item)}
              onContextMenu={(event) => handleRowContextMenu(item, event)}
            >
              <div className="absolute right-2 top-2 z-10">
                <input
                  type="checkbox"
                  checked={selectedTableKeys.includes(item.uid)}
                  onChange={() => {
                    setSelectedTableKeys(
                      selectedTableKeys.includes(item.uid)
                        ? selectedTableKeys.filter((key) => key !== item.uid)
                        : [...selectedTableKeys, item.uid]
                    );
                  }}
                  onClick={(e) => e.stopPropagation()} // evita il click sul Card
                  className={clsx(
                    'accent-blue-600 h-5 w-5 cursor-pointer',
                    selectedTableKeys.includes(item.uid)
                      ? 'block'
                      : 'hidden group-hover:block'
                  )}
                />
              </div>
              <div className="flex flex-col items-center">
                <FileIcon
                  isShortcut={item.isShortcut}
                  // isFavourite={item.isFavourite}
                  isFolder={item.isFolder}
                  fileExtension={removeShortcutExtension(item.fileExtension)}
                  className="h-20 w-20"
                />
                <p
                  className="mt-2 w-full cursor-pointer truncate text-center hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileRowClick(item, true, e);
                  }}
                >
                  <TruncatedTooltip>
                    {removeShortcutExtension(getFileTitle(item))}
                  </TruncatedTooltip>
                </p>
              </div>
            </Card>
          </List.Item>
        )}
      />
    </div>
  );
};

export default FilesList;
